import { memo, useCallback, useRef, useState } from 'react'
import {
  Box,
  Button,
  Input,
  InputLeftElement,
  InputGroup,
  type ButtonProps,
  InputRightElement
} from '@chakra-ui/react'

import { Trash2 } from 'lucide-react'

interface CircleButtonProps extends ButtonProps {
  icon: any
  label: string
  hoverWidth: string
  showHover?: boolean
}

const CircleButton = memo(
  ({
    icon,
    label,
    hoverWidth,
    showHover = false,
    ...rest
  }: CircleButtonProps) => (
    <Button
      display="inline-block"
      variant="circle"
      aria-label={label}
      aspectRatio={1}
      minWidth="80px"
      textAlign="left"
      width={showHover ? hoverWidth : '80px'}
      transition="all 0.5s"
      _hover={{
        width: hoverWidth,
        span: {
          opacity: 1
        }
      }}
      overflow="hidden"
      height="80px"
      p="18px"
      borderRadius="60px"
      colorScheme="yellow"
      color="gray.700"
      fontSize="36px"
      sx={{
        svg: {
          display: 'inline-block',
          width: '45px',
          height: 'auto',
          mt: '0px',
          ml: '0px',
          verticalAlign: 'middle'
        }
      }}
      {...rest}
    >
      <Box as={icon} />
      <Box
        as="span"
        verticalAlign="middle"
        opacity={showHover ? 1 : 0}
        className="text"
        color="gray.700"
        ml="22px"
      >
        {label}
      </Box>
    </Button>
  )
)

CircleButton.displayName = 'CircleButton'

const MultiInput = memo(
  ({
    showNumbers = false,
    onChange,
    value
  }: {
    onChange: (value: string[]) => void
    value: string[]
    showNumbers?: boolean
  }) => {
    const [inputValue, setInputValue] = useState(value ?? [])
    const inputRefs = useRef<HTMLInputElement[]>([])

    const handleChange = useCallback(
      (index: number, value: string) => {
        const newInputValue = [...inputValue]
        newInputValue[index] = value
        setInputValue(newInputValue)
      },
      [inputValue]
    )

    const handleBlur = useCallback(() => {
      onChange(inputValue)
    }, [inputValue, onChange])

    const handleRemove = useCallback(
      (index: number) => {
        const newInputValue = [...inputValue]
        newInputValue.splice(index, 1)
        setInputValue(newInputValue)
        onChange(newInputValue)
      },
      [inputValue]
    )

    return (
      <Box>
        {[...inputValue, null].map((value, index) => {
          return (
            <Box key={index} mb="5px" className="multi-input-box">
              <InputGroup size={{ base: 'xs', '2xl': 'sm' }}>
                <InputLeftElement
                  pointerEvents="none"
                  color="gray.300"
                  fontSize="1.2em"
                >
                  {showNumbers
                    ? index + 1
                    : String.fromCharCode(97 + index).toUpperCase()}
                </InputLeftElement>
                <Input
                  ref={(el) => {
                    if (el != null) inputRefs.current[index] = el
                  }}
                  value={value ?? ''}
                  onChange={(e) => {
                    handleChange(index, e.target.value)
                  }}
                  onBlur={handleBlur}
                />
                {value !== null && (
                  <InputRightElement>
                    <Box
                      as="button"
                      h="1.75rem"
                      onClick={() => {
                        handleRemove(index)
                      }}
                    >
                      <Trash2 />
                    </Box>
                  </InputRightElement>
                )}
              </InputGroup>
            </Box>
          )
        })}
      </Box>
    )
  }
)

MultiInput.displayName = 'MultiInput'

export { CircleButton, MultiInput, type CircleButtonProps }
